import { useState, useEffect, useMemo, Dispatch, SetStateAction } from 'react';
import { InfoIcon, Layers } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  IPostDetailResponse,
  IPostAttachment,
  IControllerType,
} from '@/redux/post/post.d';
import {
  filterAttachmentsForCarousel,
  resolveToolSpecs,
  isModelFile,
} from '@/redux/post/post.utils';
import { AttachmentType } from '@/redux/attachment/attachment.d';
import { BitGridCard } from '@/components/BitDetailDialog';

function ToolsDataCard({
  post,
  modelIndex,
  setModelIndex,
  selectedController,
  filteredAttachments,
}: {
  post: IPostDetailResponse;
  modelIndex: number;
  setModelIndex: Dispatch<SetStateAction<number>>;
  selectedController: IControllerType | null;
  filteredAttachments: IPostAttachment[];
}) {
  const { modelGroups } = post;
  const [showRelatedTools, setShowRelatedTools] = useState<boolean>(false);

  // Flatten modelGroups into toolData array for display
  const allToolData = useMemo(
    () => modelGroups?.flatMap((group) => group.toolData) || [],
    [modelGroups]
  );

  // Filter toolData by controller - show tools from groups that contain models of the selected controller
  const controllerFilteredToolData = useMemo(() => {
    if (!selectedController || !modelGroups) return allToolData;

    // Get model IDs from filtered attachments (models for selected controller)
    const filteredModelIds = new Set(
      filteredAttachments
        .filter((att) => isModelFile(att.fileName))
        .map((att) => att._id)
    );

    // Find groups that contain at least one model matching the selected controller
    const relevantGroups = modelGroups.filter((group) =>
      group.modelIds.some((modelId) => filteredModelIds.has(modelId))
    );

    // Return all tool data from those groups
    return relevantGroups.flatMap((group) => group.toolData);
  }, [allToolData, selectedController, filteredAttachments, modelGroups]);

  const [filteredToolData, setFilteredToolData] = useState(
    controllerFilteredToolData
  );

  useEffect(() => {
    if (!post) return;
    const carouselAttachments =
      filterAttachmentsForCarousel(filteredAttachments);
    if (carouselAttachments.length === 0) {
      setFilteredToolData(controllerFilteredToolData);
      return;
    }
    const currentAttachment = carouselAttachments[modelIndex];
    if (currentAttachment?.resourceType !== AttachmentType.model) {
      setShowRelatedTools(false);
      setFilteredToolData(controllerFilteredToolData);
      return;
    }
    const filteredData = showRelatedTools
      ? controllerFilteredToolData.filter(
          (tool) => tool.modelId === currentAttachment._id
        )
      : controllerFilteredToolData;
    setFilteredToolData(filteredData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    modelIndex,
    controllerFilteredToolData,
    filteredAttachments,
    showRelatedTools,
    post,
  ]);

  const handleCheckboxChange = () => {
    setShowRelatedTools(!showRelatedTools);
  };

  if (!allToolData.length) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <></>;
  }

  return (
    <Card className="my-5">
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex gap-2 items-center">
          <Layers className="h-5 w-5" />
          <CardTitle>Tools Data</CardTitle>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-muted-foreground flex gap-2 items-center">
            <InfoIcon size="18px" />
            Filter based on selected model
          </span>
          <Switch
            checked={showRelatedTools}
            onCheckedChange={handleCheckboxChange}
            disabled={
              filterAttachmentsForCarousel(filteredAttachments)[modelIndex]
                ?.resourceType !== AttachmentType.model
            }
          />
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3">
          {Array.isArray(filteredToolData) &&
            filteredToolData.map((tool, index) => {
              const carouselAttachments =
                filterAttachmentsForCarousel(filteredAttachments);

              // Find the group that contains this tool
              const toolGroup = modelGroups?.find((group) =>
                group.toolData.some((t) => t._id === tool._id)
              );

              // Find the model file from the current controller within the same group
              // If the tool's modelId is not in filteredAttachments, find another model from the same group
              let matchedAttachment = carouselAttachments.find(
                (attachment) => attachment._id === tool.modelId
              );

              // If not found, look for any model from the same group in the filtered attachments
              if (!matchedAttachment && toolGroup) {
                matchedAttachment = carouselAttachments.find((attachment) =>
                  toolGroup.modelIds.includes(attachment._id)
                );
              }

              const specs = resolveToolSpecs(tool, tool.bit);
              const speedCaption = specs.spindleSpeed
                ? `${specs.spindleSpeed} RPM`
                : null;

              const onViewModel = matchedAttachment
                ? () => {
                    const currentModelIndex = filterAttachmentsForCarousel(
                      filteredAttachments
                    ).findIndex(
                      (attachment) => attachment._id === matchedAttachment?._id
                    );
                    setModelIndex(currentModelIndex);
                  }
                : undefined;

              return (
                <BitGridCard
                  key={tool._id}
                  cardNumber={index + 1}
                  bit={tool.bit}
                  fileName={matchedAttachment?.fileName}
                  spindleSpeed={speedCaption || undefined}
                  onViewModel={onViewModel}
                />
              );
            })}
        </div>
      </CardContent>
    </Card>
  );
}

export default ToolsDataCard;
